// Question folders are exams (parent null) and their subjects (parent = the exam's uuid, subject = the
// catalog subject, name = the subject's name). Questions and passages live in subjects only. Each subject
// in GET question_folders/ carries the user's `role` there: "admin" for admins, else moderator / expert /
// author (sees only their own questions) / viewer.

export const ROLES = ["moderator", "expert", "author", "viewer"];

export const isExamFolder = (folder) => !!folder && !folder.parent;
export const isSubjectFolder = (folder) => !!folder?.parent;

// The account's global role (my_account.role.code).
export const isAdminUser = (user) => user?.role?.code === "ADMIN";

// What a role allows in its subject (the backend checks it again).
// Experts write questions too (2026-10-28): the server gives the subject role "expert" the create right.
export const canCreateIn = (role) => role === "admin" || role === "moderator" || role === "author" || role === "expert";
export const canShareFolder = (role) => role === "admin" || role === "moderator";
export const canEditMatrix = canShareFolder;
export const canReviewIn = (role) => role === "admin" || role === "moderator" || role === "expert";
// Who decides a question's status in a subject: its moderators and admins. Its experts only vote (like / dislike).
export const canDecideIn = (role) => role === "admin" || role === "moderator";
export const canEditAllIn = canReviewIn;
// A subject's variants (the BTS ones) are built by its moderator and admins.
export const canBuildVariants = canShareFolder;
// Tasks (TZ 2.2): its moderator and admins give them; an author or an expert sees their own, a viewer none.
export const canAssignIn = canShareFolder;
export const canSeeTasksIn = (role) => canAssignIn(role) || role === "author" || role === "expert";

// A subject's stream: "kk" (КАЗ) or "ru" (РУС); null serves both. A subject with a stream has it in its name too
// ("Kazakhstan History · РУС").
export const STREAMS = ["kk", "ru"];

// {exams: [{...exam, subjects: [...]}], byUuid: Map} from the flat list; a subject whose exam is not in
// the list is left out.
export function folderTree(list) {
  const byUuid = new Map((list || []).map((f) => [f.uuid, f]));
  const exams = (list || []).filter(isExamFolder).map((exam) => ({ ...exam, subjects: [] }));
  const examByUuid = new Map(exams.map((e) => [e.uuid, e]));
  (list || []).forEach((f) => {
    if (isSubjectFolder(f)) examByUuid.get(f.parent)?.subjects.push(f);
  });
  return { exams, byUuid };
}

// "ТАТ · Biology". Takes the nested folder of a question / passage ({name, parent: {uuid, name}}) or a
// list item, whose `parent` is the exam's uuid (then `byUuid` finds the exam's name).
export function folderPath(folder, byUuid) {
  if (!folder) return "";
  const parent = folder.parent;
  const examName = typeof parent === "object" ? parent?.name : byUuid?.get(parent)?.name;
  return examName ? `${examName} · ${folder.name}` : folder.name || "";
}

// Filter options grouped by exam: the whole exam ("ТАТ · <allLabel>", its uuid filters all its subjects),
// then each subject.
export const folderFilterGroups = (list, allLabel) =>
  folderTree(list).exams.map((exam) => ({
    label: exam.name,
    options: [
      { value: exam.uuid, label: `${exam.name} · ${allLabel}` },
      ...exam.subjects.map((s) => ({ value: s.uuid, label: s.name })),
    ],
  }));

// react-select options grouped by exam: [{label: exam, options: [{value: subject uuid, label, folder}]}],
// with only the subjects `filter(subject)` keeps; an exam left without subjects is dropped.
export function subjectFolderOptions(list, filter = () => true) {
  return folderTree(list)
    .exams.map((exam) => ({
      label: exam.name,
      options: exam.subjects.filter(filter).map((s) => ({ value: s.uuid, label: s.name, folder: s })),
    }))
    .filter((group) => group.options.length > 0);
}
